// frontend/src/components/BackendSwitcher.jsx
// Backend Switcher Component for Alertique Frontend
// Allows switching between backend locations (local/remote) and database types (SQLite/PostgreSQL)

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext.jsx";
import {
  getBackendInfo,
  switchBackendLocation,
  switchDatabaseType,
  currentBackendLocation,
  currentDatabaseType,
  getAvailableBackendLocations,
  getAvailableDatabaseTypes,
} from "../config/api.js";
import { getDatabaseMode } from "../services/system.js";
import NoPermission from "./NoPermission";

const BackendSwitcher = () => {
  const [databaseMode, setDatabaseMode] = useState("production");
  const [testResults, setTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isLoadingMode, setIsLoadingMode] = useState(false); // Add loading flag

  const { isAuthenticated, jwtToken, isLoading, canAccessAdmin } = useAuth();
  if (isLoading || !isAuthenticated) return null;
  if (!canAccessAdmin())
    return (
      <NoPermission message="You do not have permission to access the Backend Switcher." />
    );

  // Load database mode on component mount, but only if authenticated and JWT token is available
  useEffect(() => {
    console.log("BackendSwitcher: useEffect triggered", {
      isAuthenticated,
      hasJwtToken: !!jwtToken,
    });

    if (isAuthenticated && jwtToken) {
      // Add a small delay to ensure JWT token is properly stored in localStorage
      const timer = setTimeout(() => {
        console.log("BackendSwitcher: Loading database mode");
        loadDatabaseMode();
      }, 100);

      // Listen for database mode changes from other components
      const handleDatabaseModeChange = () => {
        loadDatabaseMode();
      };

      window.addEventListener("databaseModeChanged", handleDatabaseModeChange);

      return () => {
        clearTimeout(timer);
        window.removeEventListener(
          "databaseModeChanged",
          handleDatabaseModeChange
        );
      };
    } else {
      console.log("BackendSwitcher: Skipping database mode load", {
        isAuthenticated,
        hasJwtToken: !!jwtToken,
        jwtTokenLength: jwtToken ? jwtToken.length : 0,
      });
    }
  }, [isAuthenticated, jwtToken]);

  const loadDatabaseMode = async () => {
    if (isLoadingMode) return; // Prevent duplicate calls

    setIsLoadingMode(true);
    try {
      const result = await getDatabaseMode(jwtToken);
      if (result.success) {
        setDatabaseMode(result.database_mode);
      }
    } catch (error) {
      // Silently handle authentication errors (401, session expired, etc.)
      if (
        error.message &&
        (error.message.includes("401") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("session has expired") ||
          error.message.includes("session expired") ||
          error.validationResult) // Session validation errors from withSessionValidation
      ) {
        // Token expired or not authenticated - this is expected, don't log
        return;
      }
      // Log other errors
      console.error("Failed to load database mode:", error);
    } finally {
      setIsLoadingMode(false);
    }
  };

  const handleBackendChange = (backendLocation) => {
    if (databaseMode === "testing") {
      alert(
        "Environment switching is disabled while in testing mode. Please switch to production database first."
      );
      return;
    }
    switchBackendLocation(backendLocation);
    setTestResults(null); // Clear previous test results
  };

  const handleDatabaseChange = (databaseType) => {
    if (databaseMode === "testing") {
      alert(
        "Database type switching is disabled while in testing mode. Please switch to production database first."
      );
      return;
    }
    switchDatabaseType(databaseType);
    setTestResults(null); // Clear previous test results
  };

  const testAPI = async () => {
    setIsTesting(true);
    setTestResults(null);

    const backendInfo = getBackendInfo();
    const results = {
      backend: backendInfo.backendName,
      database: backendInfo.databaseName,
      baseURL: backendInfo.baseURL,
      tests: [],
    };

    try {
      // Test 1: Health endpoint
      try {
        const healthResponse = await fetch(`${backendInfo.baseURL}/health`);
        const healthData = await healthResponse.json();
        results.tests.push({
          endpoint: "/health",
          status: healthResponse.status,
          success: healthResponse.ok,
          response: healthResponse.ok
            ? "✅ Healthy"
            : `HTTP ${healthResponse.status}`,
        });
      } catch (error) {
        results.tests.push({
          endpoint: "/health",
          status: "ERROR",
          success: false,
          response: error.message,
        });
      }

      // Test 2: Categories endpoint
      try {
        const categoriesResponse = await fetch(
          `${backendInfo.baseURL}/distinct_categories`
        );
        const categoriesData = await categoriesResponse.json();
        results.tests.push({
          endpoint: "/distinct_categories",
          status: categoriesResponse.status,
          success: categoriesResponse.ok,
          response: categoriesResponse.ok
            ? `${categoriesData.length} categories`
            : `HTTP ${categoriesResponse.status}`,
        });
      } catch (error) {
        results.tests.push({
          endpoint: "/distinct_categories",
          status: "ERROR",
          success: false,
          response: error.message,
        });
      }

      // Test 3: Deals endpoint (limited to 1 item for speed)
      try {
        const dealsResponse = await fetch(
          `${backendInfo.baseURL}/deals?limit=1`
        );
        const dealsData = await dealsResponse.json();
        results.tests.push({
          endpoint: "/deals",
          status: dealsResponse.status,
          success: dealsResponse.ok,
          response: dealsResponse.ok
            ? `${dealsData.total} total deals`
            : `HTTP ${dealsResponse.status}`,
        });
      } catch (error) {
        results.tests.push({
          endpoint: "/deals",
          status: "ERROR",
          success: false,
          response: error.message,
        });
      }
    } catch (error) {
      results.tests.push({
        endpoint: "General",
        status: "ERROR",
        success: false,
        response: error.message,
      });
    }

    setTestResults(results);
    setIsTesting(false);
  };

  const backendLocations = getAvailableBackendLocations();
  const databaseTypes = getAvailableDatabaseTypes();

  // Disable database selector when using remote backend or in testing mode
  const isRemoteBackend = currentBackendLocation === "remote";
  const isInTestingMode = databaseMode === "testing";

  return (
    <div className="flex items-center space-x-2">
      {/* Backend Location Selector */}
      <select
        value={currentBackendLocation}
        onChange={(e) => handleBackendChange(e.target.value)}
        disabled={isInTestingMode}
        className={`px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-400 ${
          isInTestingMode
            ? "bg-gray-500 text-gray-400 border-gray-500 cursor-not-allowed"
            : "bg-gray-700 text-white border-gray-600"
        }`}
        title={
          isInTestingMode
            ? "Environment switching disabled while in testing mode"
            : "Switch backend location"
        }
      >
        {backendLocations.map((backend) => (
          <option key={backend.key} value={backend.key}>
            {backend.key === "local" ? "🏠" : "☁️"} {backend.name}
          </option>
        ))}
      </select>

      {/* Database Type Selector */}
      <select
        value={currentDatabaseType}
        onChange={(e) => handleDatabaseChange(e.target.value)}
        disabled={isRemoteBackend || isInTestingMode}
        className={`px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-400 ${
          isRemoteBackend || isInTestingMode
            ? "bg-gray-500 text-gray-400 border-gray-500 cursor-not-allowed"
            : "bg-gray-700 text-white border-gray-600"
        }`}
        title={
          isRemoteBackend
            ? "Database switching only available with local backend"
            : isInTestingMode
            ? "Database type switching disabled while in testing mode"
            : "Switch database type"
        }
      >
        {databaseTypes.map((db) => (
          <option key={db.key} value={db.key}>
            {db.key === "sqlite" ? "💾" : "🗄️"} {db.name}
          </option>
        ))}
      </select>

      {/* Test API Button */}
      <button
        onClick={testAPI}
        disabled={isTesting}
        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Test API connectivity"
      >
        {isTesting ? "🔄 Testing..." : "🧪 Test API"}
      </button>

      {/* Test Results Popup */}
      {testResults && (
        <div className="absolute top-full mt-2 right-0 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-80 z-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">API Test Results</h4>
            <button
              onClick={() => setTestResults(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div className="text-xs text-gray-600 mb-2">
            <strong>{testResults.backend}</strong> with{" "}
            <strong>{testResults.database}</strong>
            <br />
            {testResults.baseURL}
          </div>

          <div className="space-y-2">
            {testResults.tests.map((test, index) => (
              <div
                key={`test-${index}-${test.endpoint}`}
                className="flex items-center justify-between"
              >
                <span className="text-gray-700">{test.endpoint}:</span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    test.success
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {test.success ? "✅" : "❌"} {test.response}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendSwitcher;
